import { createContext, useContext, useEffect, useState } from 'react'

import type { ConnectionStatus } from '../connection/connectionSlice'
import { useLinkOwnedByScreen } from './board'
import { dataAgeText, linkDownText, linkOfflineText, linkStaleText } from './linkTexts'
import { formatSpan } from './time'
import { HEALTH_TIMINGS } from './timings'

/**
 * Связь с сервером глазами экрана продажи (RP-SALE-FAST, шаг 5): то, что нужно строке «нет связи 12 с · попытка 3 · повторить сейчас»
 * и возрасту данных. Пакеты не знают `RootState`, поэтому оболочка отдаёт срез `connection` контекстом (как доску сигналов), а экран
 * читает его хуком {@link useLinkStrip}.
 */
export interface ServerLink {
  status: ConnectionStatus
  /** С какого момента (мс) связи нет; `null` — связь есть. */
  since: number | null
  /** Сколько проверок доступности уже назначено. */
  attempts: number
  /** Когда (мс) сервер ответил в последний раз; `null` — ни разу. */
  lastOkAt: number | null
  /** Проверить сервер сейчас, не дожидаясь паузы. */
  retryNow: () => void
}

/** Связь оболочки; `null` — вне оболочки: строки связи нет. */
export const ServerLinkContext = createContext<ServerLink | null>(null)

/** Что показывает строка связи внутри продажи. */
export interface LinkStripView {
  /** `offline` — нет сети на устройстве, `down` — сервер не отвечает, `stale` — сервер молчит дольше порога, итога запроса ещё нет. */
  kind: 'offline' | 'down' | 'stale'
  text: string
  /** «Повторить сейчас»; `null` — спрашивать сервер нечем (связь формально есть). */
  retry: (() => void) | null
}

/**
 * Строка связи по состоянию на момент `now`; `null` — сказать нечего (в норме строка не рисуется). Возраст данных — от последнего
 * ответа сервера, а не от итога запроса: он растёт, пока запрос висит. Он виден, когда превысил `HEALTH_TIMINGS.dataStaleAfterMs`;
 * при живой связи — только если экран ждёт опроса (`watchAge`: ход оплаты читается раз в секунду), иначе тишина — норма.
 */
export function linkStripOf(link: ServerLink, now: number, watchAge: boolean): LinkStripView | null {
  const ageMs = link.lastOkAt === null ? null : Math.max(0, now - link.lastOkAt)
  const age = ageMs !== null && ageMs >= HEALTH_TIMINGS.dataStaleAfterMs ? dataAgeText(formatSpan(ageMs)) : null
  if (link.status === 'online') {
    return watchAge && age !== null ? { kind: 'stale', text: linkStaleText(age), retry: null } : null
  }
  const span = formatSpan(link.since === null ? 0 : now - link.since)
  return link.status === 'offline'
    ? { kind: 'offline', text: linkOfflineText(span, link.attempts, age), retry: link.retryNow }
    : { kind: 'down', text: linkDownText(span, link.attempts, age), retry: link.retryNow }
}

/**
 * Строка связи для экрана, который сам показывает связь (объявил это `useScreenOwnsLink`): `null` — показывать нечего или никто
 * связь не объявлял (тогда её показывает общее окно). Время пересчитывается раз в `linkTickMs`, пока строка может понадобиться.
 */
export function useLinkStrip(watchAge: boolean): LinkStripView | null {
  const link = useContext(ServerLinkContext)
  const owned = useLinkOwnedByScreen()
  const [now, setNow] = useState(() => Date.now())
  const ticking = owned && link !== null && (link.status !== 'online' || watchAge)
  useEffect(() => {
    if (!ticking) return undefined
    const tick = () => setNow(Date.now())
    const timer = window.setInterval(tick, HEALTH_TIMINGS.linkTickMs)
    const first = window.setTimeout(tick, 0)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(first)
    }
  }, [ticking])
  return owned && link !== null ? linkStripOf(link, now, watchAge) : null
}
